import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, Navigate } from 'react-router-dom';
import { Clock, ArrowLeft, ArrowRight, Calendar } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { base44 } from '@/api/base44Client';
import Navbar from '@/components/smartpos/Navbar';
import Footer from '@/components/smartpos/Footer';
import FloatingCTA from '@/components/smartpos/FloatingCTA';
import JsonLd from '@/components/smartpos/JsonLd';
import { Image } from '@/components/ui/image';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import ErrorState from '@/components/smartpos/ErrorState';
import { useLanguage } from '@/lib/LanguageContext';
import { normalizeMarkdown } from '@/lib/normalizeMarkdown';
import { legacyBlogSlug } from '@/lib/blogRedirects';
import { localizePath } from '@/lib/langPath';

const ui = {
  ES: { notFound: 'Artículo no encontrado', backToBlog: 'Volver al blog', blog: 'Blog', min: 'min', related: 'Artículos relacionados', ctaTitle: '¿Listo para modernizar tu negocio?', ctaDesc: 'Pide tu demo gratuita y un experto te llama hoy mismo.', ctaBtn: 'Solicitar demo', metaDefault: 'Artículo | Blog SmartPOS', metaDescDefault: 'Artículo del blog SmartPOS', home: 'Inicio' },
  EN: { notFound: 'Article not found', backToBlog: 'Back to blog', blog: 'Blog', min: 'min', related: 'Related articles', ctaTitle: 'Ready to modernize your business?', ctaDesc: 'Request your free demo and an expert calls you today.', ctaBtn: 'Request demo', metaDefault: 'Article | Blog SmartPOS', metaDescDefault: 'SmartPOS blog article', home: 'Home' },
  CA: { notFound: 'Article no trobat', backToBlog: 'Tornar al blog', blog: 'Blog', min: 'min', related: 'Articles relacionats', ctaTitle: 'A punt per modernitzar el teu negoci?', ctaDesc: 'Demana la teva demo gratuïta i un expert et truca avui mateix.', ctaBtn: 'Sol·licita demo', metaDefault: 'Article | Blog SmartPOS', metaDescDefault: 'Article del blog SmartPOS', home: 'Inici' },
  FR: { notFound: 'Article non trouvé', backToBlog: 'Retour au blog', blog: 'Blog', min: 'min', related: 'Articles similaires', ctaTitle: 'Prêt à moderniser votre entreprise ?', ctaDesc: 'Demandez votre démo gratuite et un expert vous appelle aujourd\'hui.', ctaBtn: 'Demander une démo', metaDefault: 'Article | Blog SmartPOS', metaDescDefault: 'Article du blog SmartPOS', home: 'Accueil' },
};

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t: tGlobal } = useLanguage();
  const t = ui[lang] || ui.ES;
  // Dirección antigua de un artículo renombrado: se atiende con la definitiva.
  const legacySlug = legacyBlogSlug(slug);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [related, setRelated] = useState([]);
  const [relatedError, setRelatedError] = useState(false);

  const loadPost = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await base44.entities.BlogPost.filter({ slug, published: true }, '-published_date', 1);
      setPost(data[0] || null);
    } catch {
      setPost(null);
      setError(true);
    } finally { setLoading(false); }
  };

  const loadRelated = async () => {
    if (!post) return;
    setRelatedError(false);
    try {
      const data = await base44.entities.BlogPost.filter({ published: true, category: post.category }, '-published_date', 4);
      setRelated(data.filter(p => p.id !== post.id).slice(0, 3));
    } catch { setRelatedError(true); }
  };

  useEffect(() => { if (!legacySlug) loadPost(); }, [slug]);
  useEffect(() => { loadRelated(); }, [post]);

  const parseFAQ = (content) => {
    if (!content) return [];
    const faqs = [];
    const regex = /###\s+(?:\d+\.?\s*)?(.+?\?)\s*\n+([\s\S]*?)(?=\n###\s|\n##\s|$)/g;
    let match;
    while ((match = regex.exec(content)) !== null) {
      const question = match[1].trim();
      const answer = match[2].trim().replace(/[*_`#]/g, '').trim();
      if (question && answer) faqs.push({ question, answer });
    }
    return faqs;
  };

  useDocumentMeta({
    title: post ? `${post.title} | Blog SmartPOS` : t.metaDefault,
    description: post?.excerpt || t.metaDescDefault,
    canonicalPath: `/blog/${legacySlug || slug}`,
    ogImage: post?.cover_image,
  });

  const content = post ? normalizeMarkdown(post.content) : '';
  const faqItems = content ? parseFAQ(content) : [];
  const jsonLd = post ? {
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'Article', headline: post.title, description: post.excerpt, image: post.cover_image, datePublished: post.published_date, dateModified: post.updated_date ? post.updated_date.split('T')[0] : post.published_date, author: { '@type': 'Organization', name: post.author || 'SmartPOS' }, publisher: { '@type': 'Organization', name: 'SmartPOS', url: 'https://www.smartpos.es' }, mainEntityOfPage: `https://www.smartpos.es/blog/${post.slug}` },
      { '@type': 'BreadcrumbList', itemListElement: [
        { '@type': 'ListItem', position: 1, name: t.home, item: 'https://www.smartpos.es' },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://www.smartpos.es/blog' },
        { '@type': 'ListItem', position: 3, name: post.title, item: `https://www.smartpos.es/blog/${post.slug}` },
      ]},
      ...(faqItems.length > 0 ? [{ '@type': 'FAQPage', mainEntity: faqItems.map(f => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })) }] : []),
    ],
  } : null;

  // Enlaces ya publicados que apuntan a la dirección antigua: se lleva al
  // artículo en su dirección definitiva, sin aviso de página inexistente.
  if (legacySlug) {
    return <Navigate to={localizePath(`/blog/${legacySlug}`, lang)} replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen font-body bg-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-32">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-24 bg-gray-100 rounded" />
            <div className="h-10 w-3/4 bg-gray-100 rounded" />
            <div className="h-64 w-full bg-gray-100 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen font-body bg-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-32">
          <ErrorState
            message={tGlobal('data.loadError')}
            retryLabel={tGlobal('data.retry')}
            onRetry={loadPost}
          />
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen font-body bg-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="font-heading font-light text-3xl text-gray-900 mb-4">{t.notFound}</h1>
          <Link to="/blog" className="inline-flex items-center gap-2 text-brand-blue font-bold hover:gap-3 transition-all">
            <ArrowLeft size={16} /> {t.backToBlog}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen font-body bg-white">
      {jsonLd && <JsonLd data={jsonLd} />}
      <Navbar />
      <main id="contenido-principal">
      <article className="pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center gap-2 text-gray-400 hover:text-brand-blue text-sm font-medium mb-8 transition-colors">
            <ArrowLeft size={15} /> {t.blog}
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-bold text-brand-blue">{post.category}</span>
            <span className="text-gray-400 text-xs flex items-center gap-1"><Calendar size={11} /> {post.published_date}</span>
            <span className="text-gray-400 text-xs flex items-center gap-1"><Clock size={11} /> {post.read_minutes || 5} {t.min}</span>
          </div>

          <h1 className="font-heading font-light text-3xl lg:text-4xl text-gray-900 mb-5 leading-tight tracking-tight">{post.title}</h1>
          <p className="text-gray-500 text-lg font-light mb-8 leading-relaxed">{post.excerpt}</p>

          {post.cover_image && (
            <div className="rounded-2xl overflow-hidden mb-10 aspect-video bg-gray-50 border border-gray-100">
              <Image src={post.cover_image} alt={post.title} fittingType="fill" priority className="w-full h-full object-cover" />
            </div>
          )}

          <div className="prose-content text-gray-600 font-light leading-relaxed space-y-5 [&_h2]:text-gray-900 [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-2xl [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:text-gray-900 [&_h3]:font-bold [&_h3]:text-xl [&_h3]:mt-8 [&_h3]:mb-2 [&_p]:text-gray-600 [&_p]:leading-relaxed [&_a]:text-brand-blue [&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 [&_strong]:text-gray-900 [&_blockquote]:border-l-2 [&_blockquote]:border-brand-blue [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-500">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>

          {relatedError && (
            <div className="mt-16">
              <ErrorState
                compact
                message={tGlobal('data.loadError')}
                retryLabel={tGlobal('data.retry')}
                onRetry={loadRelated}
              />
            </div>
          )}

          {related.length > 0 && (
            <div className="mt-16 mb-4">
              <h2 className="font-heading font-bold text-2xl text-gray-900 mb-6">{t.related}</h2>
              <div className="grid sm:grid-cols-3 gap-5">
                {related.map(r => (
                  <Link key={r.id} to={`/blog/${r.slug}`} className="group rounded-xl bg-white border border-gray-100 overflow-hidden shadow-sm hover:shadow-lg hover:border-blue-200 transition-all duration-300 flex flex-col">
                    {r.cover_image && (
                      <div className="aspect-video overflow-hidden bg-gray-50">
                        <Image src={r.cover_image} alt={r.title} fittingType="fill" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    )}
                    <div className="p-4 flex flex-col flex-1">
                      <span className="text-xs font-bold text-brand-blue mb-2">{r.category}</span>
                      <h3 className="font-heading font-bold text-sm text-gray-900 leading-snug group-hover:text-brand-blue transition-colors">{r.title}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-12 p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-violet-50 border border-blue-100 text-center">
            <h2 className="font-heading font-bold text-2xl text-gray-900 mb-3">{t.ctaTitle}</h2>
            <p className="text-gray-500 font-light mb-5">{t.ctaDesc}</p>
            <button onClick={() => navigate('/demo')} className="bg-brand-blue text-white px-7 py-3.5 rounded-xl font-bold text-sm hover:bg-[#0078d4] transition-all hover:scale-105 shadow-lg shadow-blue-500/20 inline-flex items-center gap-2">
              {t.ctaBtn} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </article>
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}